import { API_HOST } from '@/constants/config';

const toQueryString = (params = {}) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.set(key, String(value));
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

const fetchTopics = async (path, params = {}) => {
  const url = `${API_HOST}${path}${toQueryString(params)}`;
  let response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Platform': 'web',
      },
    });
  } catch {
    throw new Error('Network error — unable to reach the topics service.');
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error(
      response.ok
        ? 'Topics service returned an unexpected response.'
        : `Topics service returned ${response.status}.`,
    );
  }

  const data = await response.json();
  if (!response.ok || data?.error) {
    throw new Error(data?.message || 'Failed to fetch topics data');
  }
  return data;
};

export const topicApiSsr = {
  listCategories: async () => {
    const data = await fetchTopics('/json/topics/categories');
    return data.categories || [];
  },

  listSeoSlugs: async () => {
    const data = await fetchTopics('/json/topics/seo-slugs');
    return data.slugs || [];
  },

  getBySlug: async (slug) => {
    if (!slug) return null;
    const data = await fetchTopics(`/json/topics/by-slug/${encodeURIComponent(slug)}`);
    return data.topic || null;
  },

  getRelatedBySlug: async (slug, { limit = 16 } = {}) => {
    if (!slug) return [];
    const data = await fetchTopics(
      `/json/topics/by-slug/${encodeURIComponent(slug)}/related`,
      { limit },
    );
    return Array.isArray(data?.topics) ? data.topics : [];
  },

  getByCategory: async (categorySlug, { page = 1, limit = 24, q } = {}) => {
    if (!categorySlug) return null;
    const data = await fetchTopics(
      `/json/topics/by-category/${encodeURIComponent(categorySlug)}`,
      { page, limit, q: q?.trim() || undefined },
    );
    return data || null;
  },

  /** Featured topics for footer / browse surfaces (top by conversation count). */
  listFeatured: async ({ limit = 10, categorySlug = 'person' } = {}) => {
    const data = await fetchTopics(
      `/json/topics/by-category/${encodeURIComponent(categorySlug)}`,
      { page: 1, limit },
    );
    return Array.isArray(data?.topics) ? data.topics : [];
  },
};
